import router from 'next/router';
import fetchNext from '@features/api/fetchNext';
import { IAdditionalArgument, IAdditionalArguments, IAdditionalArgumentsRecord, ICartAvailableDelivery, ICartData, TAdditionalDeliveryAddressParameter } from '@features/cart/typescript';
import { IAddressesIDs, IServicePrice, IOnlinePaymentProvider, ISessionCustomerData, ISelectedShop, ICartDeliveriesV2Response, IDeliveryDestination } from '@features/checkout/typescript';
import getPublicEnv from '@utils/getPublicEnv';
import { sortEuroDeliveries } from '@features/checkout/utils/sortEuroDeliveries';
import { getRegion } from '@utils/misc';
import { convertDeliveryItemV2ToCartAvailableDelivery, getDeliveriesForTransportGroup, getServicesForTransportGroup } from '@features/checkout/utils/deliveryV2Adapter';
class NextCheckoutApi {
  public endpoints = {
    createCart: () => `cart/create`,
    getCart: (cartId: string) => `cart/${cartId}`,
    changeCartLanguage: (cartId: string) => `cart/${cartId}/language`,
    getAvailableDeliveries: (cartId: string, transportGroupId: string, additionalArguments?: IAdditionalArgument | IAdditionalArguments | IAdditionalArgumentsRecord) => {
      const baseURI = `cart/${cartId}/transport/${transportGroupId}/available-deliveries`;
      if (!additionalArguments) return baseURI;

      // Obsługa różnych typów argumentów
      if ('key' in additionalArguments && 'value' in additionalArguments) {
        // Pojedynczy IAdditionalArgument
        return `${baseURI}?additionalArgumentKey=${additionalArguments.key}&additionalArgumentValue=${additionalArguments.value}`;
      } else if ('arguments' in additionalArguments && Array.isArray(additionalArguments.arguments)) {
        // IAdditionalArguments (tablica)
        const queryParams = additionalArguments.arguments.map(arg => `${encodeURIComponent(arg.key)}=${encodeURIComponent(arg.value)}`).join('&');
        return `${baseURI}?${queryParams}`;
      } else if (typeof additionalArguments === 'object') {
        // IAdditionalArgumentsRecord (Record<string, string>)
        const queryParams = Object.entries(additionalArguments).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&');
        return `${baseURI}?${queryParams}`;
      }
      return baseURI;
    },
    addPositions: (cartId: string) => `cart/${cartId}/add`,
    deletePosition: (cartId: string, positionId: string) => `cart/${cartId}/positions/${positionId}`,
    changeQuantity: (cartId: string, positionId: string) => `cart/${cartId}/positions/${positionId}/quantity`,
    changeDelivery: (cartId: string, transportGroupId: string) => `cart/${cartId}/transport/${transportGroupId}/changeDelivery`,
    assignCustomerToCart: (cartId: string) => `cart/${cartId}/customer`,
    getAvailableDigitalTaxReceiptUsage: () => 'cart/available-digital-tax-receipt-usage',
    getTransactionMethods: () => `cart/transaction-methods`,
    createOrder: () => `cart/create-order`,
    getOrderPaymentDetails: () => `order/payment/details`,
    getOrderDetails: () => `order/details`,
    clearOrder: () => `order/clear`,
    clearSessionOrderId: () => `order/session-order-id`,
    confirmPaypalPayment: (tokenId: string) => `paypal/${tokenId}/return`,
    cancelPaypalPayment: (tokenId: string) => `paypal/${tokenId}/cancel`,
    confirmOpenpayPayment: (tokenId: string) => `openpay/${tokenId}/return`,
    confirmBNPPayment: (orderId: string, issueNumber: string) => `order/${orderId}/bnp/${issueNumber}/confirm`,
    selectedPayment: () => `checkout/selectedPayment`,
    selectedShop: () => `checkout/selectedShop`,
    selectedDigitalTaxReceiptUsageDataId: () => `checkout/selectedDigitalTaxReceiptUsageDataId`,
    checkoutAddressCollection: () => `checkout/checkoutAddressCollection`,
    sessionCustomerData: () => `checkout/customerData`,
    checkDiscount: (cartId: string) => `cart/${cartId}/discount-code`,
    selectedTaxRegimeDataId: () => `checkout/selectedTaxRegimeDataId`,
    getPointsOfDelivery: (cartId: string, location: GeolocationCoordinates | null, transportGroupId: string) => `cart/${cartId}/pointsOfDelivery?transportGroupId=${transportGroupId}&latitude=${location?.latitude || ''}&longitude=${location?.longitude || ''}`,
    getPointsOfSale: () => `cart/pointsOfSale`,
    checkBringingService: (cartId: string, transportGroupId: string) => `cart/${cartId}/transport/${transportGroupId}/bring`,
    getBringingServicePrice: (cartId: string, transportGroupId: string) => `cart/${cartId}/transport/${transportGroupId}/precalculate-service-v2/bring`,
    checkAssemblyService: (cartId: string, transportGroupId: string) => `cart/${cartId}/transport/${transportGroupId}/assembly`,
    checkExpressService: (cartId: string, transportGroupId: string) => `cart/${cartId}/transport/${transportGroupId}/express_delivery`,
    getAssemblyServicePrice: (cartId: string, transportGroupId: string) => `cart/${cartId}/transport/${transportGroupId}/precalculate-service-v2/assembly`,
    getBNPHash: () => `order/hash`,
    sessionNote: () => `checkout/note`,
    service: (cartId: string, transportGroupId: string, serviceId: string) => `cart/${cartId}/transport/${transportGroupId}/service/${serviceId}`,
    cartAgreement: (cartId: string) => `cart/${cartId}/agreement`,
    getCartDeliveries: (cartId: string, destination?: IDeliveryDestination) => {
      const baseUrl = `cart/${cartId}/deliveries`;
      if (!destination) {
        return baseUrl;
      }
      const params = new URLSearchParams();
      if (destination.postalCode) {
        params.append('destination[postalCode]', destination.postalCode);
      }
      if (destination.countryCode) {
        params.append('destination[countryCode]', destination.countryCode);
      }
      return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
    }
  };
  public getLocale() {
    const isServerSide = typeof window === 'undefined';
    if (isServerSide) return process.env.DEFAULT_LOCALE;
    const {
      locale
    } = router;
    return locale || getPublicEnv('NEXT_PUBLIC_DEFAULT_LOCALE');
  }
  public getCartLanguage() {
    const locale = this.getLocale() || '';
    return locale.split(/[-_]/)[0].toLowerCase();
  }
  public async createCart() {
    const method = 'POST';
    const language = this.getCartLanguage();
    const {
      json
    } = await fetchNext(this.endpoints.createCart(), {
      method,
      body: JSON.stringify({
        language
      })
    }, this.getLocale());
    return json;
  }
  public async changeCartLanguage(cartId: string, language: string) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.changeCartLanguage(cartId), {
      method,
      body: JSON.stringify({
        language
      })
    }, this.getLocale());
    return json;
  }
  public async getCart(cartId) {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getCart(cartId), {
      method
    }, this.getLocale());
    return json;
  }
  public async getAvailableDeliveries(cartId: string, transportGroupId: string, additionalArguments?: IAdditionalArgument | IAdditionalArguments | IAdditionalArgumentsRecord) {
    try {
      // Konwertuj argumenty na IDeliveryDestination
      let destination = this.convertAdditionalArgumentsToDestination(additionalArguments);
      if (!destination) {
        const {
          locale
        } = router;
        destination = {
          countryCode: process.env?.DEFAUL_DELIVERY_LOCALE || locale?.toUpperCase() || 'PL'
        };
      }
      const deliveriesResponse = await this.getCartDeliveries(cartId, destination);
      const cartDeliveries = getDeliveriesForTransportGroup(deliveriesResponse, cartId, transportGroupId);
      const servicesPool = deliveriesResponse.availableServices[cartId] ?? deliveriesResponse.availableServices[transportGroupId] ?? [];
      const services = getServicesForTransportGroup(deliveriesResponse, cartId, transportGroupId);
      if (!cartDeliveries || !Array.isArray(cartDeliveries)) {
        return {
          success: true,
          data: [],
          services: [],
          servicesPool
        };
      }
      const convertedDeliveries: ICartAvailableDelivery[] = sortEuroDeliveries(cartDeliveries.map(deliveryItem => convertDeliveryItemV2ToCartAvailableDelivery(deliveryItem, destination?.postalCode || '', servicesPool)), getRegion('uppercase'));
      return {
        success: true,
        data: convertedDeliveries,
        services,
        servicesPool
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        services: [],
        servicesPool: [],
        message: error instanceof Error ? error.message : 'Nieznany błąd podczas pobierania dostaw'
      };
    }
  }
  public async addPositions(cartId: string, productArray: ICartData[]) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.addPositions(cartId), {
      method,
      body: JSON.stringify({
        products: productArray
      })
    }, this.getLocale());
    return json;
  }
  public async deletePosition(cartId: string, positionId: string) {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.deletePosition(cartId, positionId), {
      method
    }, this.getLocale());
    return json;
  }
  public async changePositionQuantity(cartId: string, positionId: string, quantity: number) {
    const method = 'PATCH';
    const {
      json
    } = await fetchNext(this.endpoints.changeQuantity(cartId, positionId), {
      method,
      body: JSON.stringify({
        quantity
      })
    });
    return json;
  }
  public async changeDelivery(cartId: string, delivery: ICartAvailableDelivery, transportGroupId: string, additionalDeliveryAddressParameters?: TAdditionalDeliveryAddressParameter) {
    const method = 'PATCH';
    const requestData: {
      delivery: ICartAvailableDelivery;
      cartId: string;
      additionalDeliveryAddressParameters?: Record<string, string>;
    } = {
      delivery,
      cartId
    };
    if (additionalDeliveryAddressParameters) {
      requestData.additionalDeliveryAddressParameters = Object.fromEntries(Object.entries(additionalDeliveryAddressParameters));
    }
    const {
      json
    } = await fetchNext(this.endpoints.changeDelivery(cartId, transportGroupId), {
      method,
      body: JSON.stringify({
        ...requestData
      })
    }, this.getLocale());
    return json;
  }
  public async assignCustomerToCart(cartId: string, customerId: string) {
    const method = 'PATCH';
    const {
      json
    } = await fetchNext(this.endpoints.assignCustomerToCart(cartId), {
      method,
      body: JSON.stringify({
        customerId
      })
    });
    return json;
  }
  public async getAvailableDigitalTaxReceiptUsage() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getAvailableDigitalTaxReceiptUsage(), {
      method
    }, this.getLocale());
    return json;
  }
  public async clearOrder() {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.clearOrder(), {
      method
    }, this.getLocale());
    return json;
  }
  public async clearSessionOrderId() {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.clearSessionOrderId(), {
      method
    }, this.getLocale());
    return json;
  }
  public async createOrder(cartId: string, customerId: string) {
    const method = 'POST';
    const body = JSON.stringify({
      cartId,
      customerId
    });
    const {
      json
    } = await fetchNext(this.endpoints.createOrder(), {
      method,
      body
    }, this.getLocale());
    return json;
  }
  public async getTransactionMethods() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getTransactionMethods(), {
      method
    }, this.getLocale());
    return json;
  }
  public async getOrderPaymentDetails() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getOrderPaymentDetails(), {
      method
    }, this.getLocale());
    return json;
  }
  public async getOrderDetails() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getOrderDetails(), {
      method
    }, this.getLocale());
    return json;
  }
  public async confirmPaypalPayment(tokenId: string) {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.confirmPaypalPayment(tokenId), {
      method
    }, this.getLocale());
    return json;
  }
  public async cancelPaypalPayment(tokenId: string) {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.cancelPaypalPayment(tokenId), {
      method
    }, this.getLocale());
    return json;
  }
  public async confirmOpenpayPayment(tokenId: string): Promise<JSON> {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.confirmOpenpayPayment(tokenId), {
      method
    }, this.getLocale());
    return json;
  }
  public async confirmBNPPayment(orderId: string, issueNumber: string) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.confirmBNPPayment(orderId, issueNumber), {
      method
    }, this.getLocale());
    return json;
  }
  public async setSelectedPayment(selectedPayment: IOnlinePaymentProvider) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.selectedPayment(), {
      method,
      body: JSON.stringify(selectedPayment)
    }, this.getLocale());
    return json;
  }
  public async getSelectedPayment() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.selectedPayment(), {
      method
    }, this.getLocale());
    return json;
  }
  public async setSelectedShop(selectedShop: ISelectedShop | null) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.selectedShop(), {
      method,
      body: JSON.stringify(selectedShop)
    }, this.getLocale());
    return json;
  }
  public async getSelectedShop() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.selectedShop(), {
      method
    }, this.getLocale());
    return json;
  }
  public async setSelectedDigitalTaxReceiptUsageDataId(selectedDigitalTaxReceiptUsageDataId: string) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.selectedDigitalTaxReceiptUsageDataId(), {
      method,
      body: JSON.stringify({
        selectedDigitalTaxReceiptUsageDataId
      })
    }, this.getLocale());
    return json;
  }
  public async getSelectedDigitalTaxReceiptUsageDataId() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.selectedDigitalTaxReceiptUsageDataId(), {
      method
    }, this.getLocale());
    return json;
  }
  public async setCheckoutAddressCollection(checkoutAddressCollection: IAddressesIDs) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.checkoutAddressCollection(), {
      method,
      body: JSON.stringify(checkoutAddressCollection)
    }, this.getLocale());
    return json;
  }
  public async getCheckoutAddressCollection() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.checkoutAddressCollection(), {
      method
    }, this.getLocale());
    return json;
  }
  public async setSessionCustomerData(customerData: ISessionCustomerData) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.sessionCustomerData(), {
      method,
      body: JSON.stringify(customerData)
    }, this.getLocale());
    return json;
  }
  public async getSessionCustomerData() {
    if (process.env.IS_CUSTOMER !== 'true') return {};
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.sessionCustomerData(), {
      method
    }, this.getLocale());
    return json;
  }
  public async checkDiscountCode(cartId: string, code: string): Promise<JSON> {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.checkDiscount(cartId), {
      method,
      body: JSON.stringify({
        code
      })
    }, this.getLocale());
    return json;
  }
  public async removeDiscountCode(cartId: string): Promise<JSON> {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.checkDiscount(cartId), {
      method
    }, this.getLocale());
    return json;
  }
  public async addBringingService(cartId: string, transportGroupId: string): Promise<{
    success: boolean;
  }> {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.checkBringingService(cartId, transportGroupId), {
      method
    }, this.getLocale());
    return json;
  }
  public async removeBringingService(cartId: string, transportGroupId: string): Promise<{
    success: boolean;
  }> {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.checkBringingService(cartId, transportGroupId), {
      method
    }, this.getLocale());
    return json;
  }
  public async getBringingServicePrice(cartId: string, transportGroupId: string): Promise<IServicePrice> {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getBringingServicePrice(cartId, transportGroupId), {
      method
    }, this.getLocale());
    return json;
  }
  public async addAssemblyService(cartId: string, transportGroupId: string): Promise<{
    success: boolean;
  }> {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.checkAssemblyService(cartId, transportGroupId), {
      method
    }, this.getLocale());
    return json;
  }
  public async removeAssemblyService(cartId: string, transportGroupId: string): Promise<{
    success: boolean;
  }> {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.checkAssemblyService(cartId, transportGroupId), {
      method
    }, this.getLocale());
    return json;
  }
  public async getAssemblyServicePrice(cartId: string, transportGroupId: string): Promise<IServicePrice[]> {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getAssemblyServicePrice(cartId, transportGroupId), {
      method
    }, this.getLocale());
    return json;
  }
  public async setSelectedTaxRegimeDataId(selectedTaxRegimeDataId: string) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.selectedTaxRegimeDataId(), {
      method,
      body: JSON.stringify({
        selectedTaxRegimeDataId
      })
    }, this.getLocale());
    return json;
  }
  public async getSelectedTaxRegimeDataId() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.selectedTaxRegimeDataId(), {
      method
    }, this.getLocale());
    return json;
  }
  public async getPointsOfDelivery(location: GeolocationCoordinates | null, cartId: string, transportGroupId: string) {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getPointsOfDelivery(cartId, location, transportGroupId), {
      method
    });
    return json.data;
  }
  public async getPointsOfSale() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getPointsOfSale(), {
      method
    });
    return json.data;
  }
  public async getBNPHash() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getBNPHash(), {
      method
    }, this.getLocale());
    return json.data;
  }
  public async setSessionNote(note: string) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.sessionNote(), {
      method,
      body: JSON.stringify(note)
    }, this.getLocale());
    return json;
  }
  public async getSessionNote() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.sessionNote(), {
      method
    }, this.getLocale());
    return json;
  }
  public async addService(cartId: string, transportGroupId: string, serviceId: string): Promise<{
    success: boolean;
  }> {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.service(cartId, transportGroupId, serviceId), {
      method
    }, this.getLocale());
    return json;
  }
  public async removeExpressService(cartId: string, transportGroupId: string): Promise<{
    success: boolean;
  }> {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.checkExpressService(cartId, transportGroupId), {
      method
    }, this.getLocale());
    return json;
  }
  public async getCartAssemblyAgreement(cartId: string) {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.cartAgreement(cartId), {
      method
    }, this.getLocale());
    return json;
  }
  public async setCartAssemblyAgreement(cartId: string) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.cartAgreement(cartId), {
      method
    }, this.getLocale());
    return json;
  }
  public async getCartDeliveries(cartId: string, destination?: IDeliveryDestination): Promise<ICartDeliveriesV2Response> {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getCartDeliveries(cartId, destination), {
      method
    }, this.getLocale());
    return json.data || json;
  }
  private convertAdditionalArgumentsToDestination(additionalArguments?: IAdditionalArgument | IAdditionalArguments | IAdditionalArgumentsRecord): IDeliveryDestination | null {
    if (!additionalArguments) return null;
    let postalCode = '';
    let countryCode = '';

    // Obsługa różnych typów argumentów
    if ('key' in additionalArguments && 'value' in additionalArguments) {
      // Pojedynczy IAdditionalArgument
      if (additionalArguments.key === 'postalCode') {
        postalCode = additionalArguments.value;
        countryCode = 'PL'; // Domyślnie PL
      } else if (additionalArguments.key === 'countryCode') {
        countryCode = additionalArguments.value;
      }
    } else if ('arguments' in additionalArguments && Array.isArray(additionalArguments.arguments)) {
      // IAdditionalArguments (tablica)
      const postalCodeArg = additionalArguments.arguments.find(arg => arg.key === 'postalCode');
      const countryCodeArg = additionalArguments.arguments.find(arg => arg.key === 'countryCode');
      postalCode = postalCodeArg?.value || '';
      countryCode = countryCodeArg?.value || 'PL';
    } else if (typeof additionalArguments === 'object' && !('key' in additionalArguments) && !('arguments' in additionalArguments)) {
      // IAdditionalArgumentsRecord (Record<string, string>)
      const recordArgs = additionalArguments;
      postalCode = recordArgs.postalCode || '';
      countryCode = recordArgs.countryCode || 'PL';
    }
    if (postalCode || countryCode) {
      return {
        postalCode,
        countryCode
      };
    }
    return null;
  }
}
export default new NextCheckoutApi();